import { useEffect, useRef, useState } from 'react';
import { Confirm, Drawer, Head, useAdmin, useDrawerRoute } from '../ui/Shell';
import { Block, Cell, Field, Grid, Kv, Pill, Table, type Act, type Row, type Tone } from '../ui/Block';
import { call, del, fmtDate, patch, post, type Env } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { confirmSimple, DBlock, DrawerBody, errText } from './dataShared';

export interface AdminArticle {
  id: string; slug: string; language: string; category: string; categoryLabel: string;
  title: string; description: string; body: string;
  coverUrl: string | null; coverAlt: string | null;
  status: 'draft' | 'published'; publishedAt: string | null; createdAt: string; updatedAt: string; readMinutes: number;
}
interface Category { key: string; label: string }
interface Listing { articles: AdminArticle[]; categories: Category[] }
interface TocEntry { id: string; text: string; children: { id: string; text: string }[] }
interface Preview { html: string; toc: TocEntry[]; readMinutes: number }

export const slugify = (s: string) => s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const toLocal = (iso: string | null) => { if (!iso) return ''; const d = new Date(iso); const p = (n: number) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };
const fromLocal = (s: string) => (s.trim() ? new Date(s).toISOString() : null);

const STATUS_TONE: Record<string, Tone> = { published: 'ok', draft: 'mute' };
const statusPill = (s: string) => ({ label: s.toUpperCase(), tone: STATUS_TONE[s] ?? 'mute' });

export const coverSrc = (env: Env, a: AdminArticle) => (a.coverUrl ? `${env.apiBase}${a.coverUrl}?v=${encodeURIComponent(a.updatedAt)}` : null);

interface Form { title: string; slug: string; slugEdited: boolean; category: string; description: string; body: string; coverAlt: string; publishedAt: string }

const fromArticle = (a: AdminArticle | null, categories: Category[]): Form => ({
  title: a?.title ?? '', slug: a?.slug ?? '', slugEdited: !!a, category: a?.category ?? categories[0]?.key ?? 'fundamentals',
  description: a?.description ?? '', body: a?.body ?? '', coverAlt: a?.coverAlt ?? '', publishedAt: toLocal(a?.publishedAt ?? null),
});

function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = useState(v);
  useEffect(() => { const id = setTimeout(() => setD(v), ms); return () => clearTimeout(id); }, [v, ms]);
  return d;
}

export function Articles() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const d = useData<Listing>(env, '/admin/articles', 30, paused);
  const list = d.data?.articles ?? [];
  const categories = d.data?.categories ?? [];
  const [status, setStatus] = useState<'' | 'draft' | 'published'>('');
  const shown = status ? list.filter((a) => a.status === status) : list;

  const editor = (a: AdminArticle | null) => <Editor initial={a} categories={categories} onChanged={d.reload} />;
  const openEditor = (a: AdminArticle | null) => ctx.openDrawer(`articles:${a?.id ?? 'new'}`, editor(a));
  useDrawerRoute('articles', (id) => { if (!d.data) return undefined; if (id === 'new') return editor(null); const a = list.find((x) => x.id === id); return a ? editor(a) : null; }, [d.data]);

  const rows: Row[] = shown.map((a) => {
    const acts: Act[] = [{ label: 'Edit', onClick: () => openEditor(a), disabled: mobile }];
    return {
      key: a.id,
      onClick: mobile ? undefined : () => openEditor(a),
      cells: [
        { t: a.title, fw: 500, sub: a.slug },
        { pill: { label: a.categoryLabel.toUpperCase(), tone: 'info' } },
        { pill: statusPill(a.status) },
        { t: a.publishedAt ? fmtDate(a.publishedAt) : '—', mono: true, fg: a.publishedAt ? undefined : 'var(--ink-40)' },
        { t: fmtDate(a.updatedAt), mono: true, sub: `${a.readMinutes} min read` },
        { acts },
      ],
    };
  });

  return (
    <>
      <Head title="Articles" sub="The website's articles: written here, served rendered by the API to vocalstack.com/articles. A draft is invisible on the site until it is published."
        acts={<button type="button" className="btn btn--sm btn--primary" disabled={mobile} onClick={() => openEditor(null)}>New article</button>} />
      <Grid>
        <Cell span={12}>
          <Block title="Articles" count={shown.length} asOf={hhmmss(d.at)} stale={d.ageS > 60} loading={d.loading && !d.data} error={describeErr(d.error) ? { ...describeErr(d.error)!, retry: d.reload } : null}
            chips={[{ label: 'Published', on: status === 'published', onClick: () => setStatus((s) => (s === 'published' ? '' : 'published')) }, { label: 'Drafts', on: status === 'draft', onClick: () => setStatus((s) => (s === 'draft' ? '' : 'draft')) }]}
            empty={d.data && shown.length === 0 ? 'No articles yet. New article starts one.' : undefined} note={mobile ? 'Editing is desktop-only.' : undefined}>
            <Table mobile={mobile} minWidth={900} rows={rows}
              cols={[{ label: 'TITLE', width: 'minmax(260px,2fr)' }, { label: 'CATEGORY', width: '140px' }, { label: 'STATUS', width: '120px' }, { label: 'PUBLISHED', width: '150px' }, { label: 'UPDATED', width: '150px' }, { label: '', width: 'minmax(80px,auto)', align: 'r' }]} />
          </Block>
        </Cell>
      </Grid>
    </>
  );
}

function Editor({ initial, categories, onChanged }: { initial: AdminArticle | null; categories: Category[]; onChanged: () => void }) {
  const ctx = useAdmin();
  const { env } = ctx;
  const [article, setArticle] = useState<AdminArticle | null>(initial);
  const [f, setF] = useState<Form>(() => fromArticle(initial, categories));
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const setTitle = (v: string) => setF((x) => ({ ...x, title: v, slug: x.slugEdited ? x.slug : slugify(v) }));

  const body = useDebounced(f.body, 400);
  useEffect(() => {
    let gone = false;
    post<Preview>(env, '/admin/articles/preview', { body })
      .then((p) => { if (!gone) { setPreview(p); setPreviewErr(null); } })
      .catch((e) => { if (!gone) setPreviewErr(errText(e)); });
    return () => { gone = true; };
  }, [env, body]);

  const slugOk = /^[a-z0-9-]+$/.test(f.slug);
  const problem = !f.title.trim() ? 'title' : !slugOk ? 'slug ([a-z0-9-]+)' : !categories.some((c) => c.key === f.category) && categories.length ? 'category' : null;

  const payload = (status: 'draft' | 'published') => ({
    title: f.title.trim(), slug: f.slug, category: f.category, description: f.description.trim(), body: f.body,
    coverAlt: f.coverAlt.trim() || null, status, publishedAt: fromLocal(f.publishedAt),
  });

  const save = async (status: 'draft' | 'published', label: string): Promise<AdminArticle | null> => {
    if (problem) return null;
    setBusy(label); setErr(null);
    try {
      const r = article
        ? await patch<{ article: AdminArticle }>(env, `/admin/articles/${article.id}`, payload(status))
        : await post<{ article: AdminArticle }>(env, '/admin/articles', payload(status));
      setArticle(r.article);
      setF((x) => ({ ...x, slug: r.article.slug, slugEdited: true, publishedAt: toLocal(r.article.publishedAt) }));
      ctx.toast(`${label}: ${r.article.title}`, status === 'published' ? 'Live on the site within a minute' : undefined);
      onChanged();
      return r.article;
    } catch (e) { setErr(errText(e)); return null; } finally { setBusy(null); }
  };

  const upload = async (file: File) => {
    setBusy('Uploading'); setErr(null);
    try {
      let a = article;
      if (!a) {
        if (problem) { setErr(`Check the ${problem} before adding a cover.`); return; }
        const r = await post<{ article: AdminArticle }>(env, '/admin/articles', payload('draft'));
        a = r.article; setArticle(a); setF((x) => ({ ...x, slug: a!.slug, slugEdited: true })); onChanged();
      }
      const form = new FormData();
      form.append('file', file, file.name);
      const r = await call<{ article: AdminArticle }>(env, `/admin/articles/${a.id}/cover`, { method: 'POST', form });
      setArticle(r.article); ctx.toast('Cover uploaded', file.name, { logged: false }); onChanged();
    } catch (e) { setErr(errText(e)); } finally { setBusy(null); if (fileRef.current) fileRef.current.value = ''; }
  };

  const removeCover = async () => {
    if (!article) return;
    setBusy('Removing'); setErr(null);
    try {
      const r = await del<{ article: AdminArticle }>(env, `/admin/articles/${article.id}/cover`);
      setArticle(r.article); set('coverAlt', ''); ctx.toast('Cover removed', undefined, { logged: false }); onChanged();
    } catch (e) { setErr(errText(e)); } finally { setBusy(null); }
  };

  const remove = () => {
    if (!article) return;
    const a = article;
    ctx.openModal(
      <Confirm env={env} mobile={ctx.mobile} app={a.slug} title={`Delete “${a.title}”`} confirmLabel="Delete" danger onClose={ctx.closeModal}
        body={a.status === 'published' ? 'The article leaves the site within a minute; its URL stops answering. This cannot be undone.' : 'The draft and its cover are deleted. This cannot be undone.'}
        onConfirm={async () => { await del(env, `/admin/articles/${a.id}`); ctx.toast(`Deleted ${a.title}`); onChanged(); ctx.closeDrawer(); }} />,
    );
  };

  const published = article?.status === 'published';
  const disabled = !!busy || !!problem;
  const cover = article ? coverSrc(env, article) : null;
  const grid: React.CSSProperties = { padding: '12px 14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px 10px' };

  return (
    <Drawer eyebrow={article ? 'ARTICLE' : 'NEW ARTICLE'} title={article?.title || f.title || 'Untitled'} wide
      sub={article ? `${article.categoryLabel} · ${article.status}${article.publishedAt ? ` · ${fmtDate(article.publishedAt)}` : ''} · updated ${fmtDate(article.updatedAt)}` : 'Saved as a draft until you publish it.'}
      chip={article ? <Pill tone={STATUS_TONE[article.status]}>{article.status.toUpperCase()}</Pill> : undefined}
      acts={
        <>
          {article && <button type="button" className="btn btn--sm btn--danger" disabled={!!busy} onClick={remove}>Delete</button>}
          {published
            ? <button type="button" className="btn btn--sm" disabled={disabled} onClick={() => save('draft', 'Unpublished')}>Unpublish</button>
            : <button type="button" className="btn btn--sm" disabled={disabled} onClick={() => save('draft', 'Saved draft')}>Save draft</button>}
          <button type="button" className="btn btn--sm btn--primary" disabled={disabled} title={problem ? `Check the ${problem}` : undefined} onClick={() => save('published', published ? 'Saved' : 'Published')}>{busy ?? (published ? 'Save' : 'Publish')}</button>
        </>
      }>
      <DrawerBody>
        {err && <DBlock bg="var(--conf-low-soft)"><div style={{ padding: '10px 14px', fontSize: 12.5 }} role="alert">{err}</div></DBlock>}
        {problem && <DBlock bg="var(--conf-mid-soft)"><div style={{ padding: '10px 14px', fontSize: 12.5 }}>Check the {problem}.</div></DBlock>}
        <DBlock title="The article">
          <div style={grid}>
            <Field label="Title" value={f.title} onChange={setTitle} placeholder="Live translation latency, explained" autoFocus={!article} />
            <Field label="Slug · the URL" mono value={f.slug} onChange={(v) => setF((x) => ({ ...x, slug: v, slugEdited: true }))} placeholder="live-translation-latency" hint={article ? `vocalstack.com/articles/${f.category}/${f.slug || '…'}/` : 'From the title until you edit it.'} />
            <label className="field">
              <span className="field__label">Category</span>
              <select className="field__input" value={f.category} onChange={(e) => set('category', e.target.value)}>
                {(categories.length ? categories : [{ key: f.category, label: f.category }]).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
            <Kv rows={[{ k: 'Language', v: { t: 'English', sub: 'Translations come later.' } }]} />
            <Field label="Published · empty: when first published" type="datetime-local" value={f.publishedAt} onChange={(v) => set('publishedAt', v)} />
          </div>
          <div style={{ padding: '0 14px 12px' }}>
            <label className="field">
              <span className="field__label">Description · the card text, the lead quote and the meta description</span>
              <textarea className="field__input" value={f.description} rows={2} style={{ resize: 'vertical' }} onChange={(e) => set('description', e.target.value)} placeholder="One or two sentences." />
            </label>
          </div>
        </DBlock>
        <DBlock title="Cover" sub="PNG, JPEG or WebP, 5 MB at most. Shown on the card, the article and link previews.">
          <div style={{ ...grid, gridTemplateColumns: 'minmax(0, 1fr) minmax(220px, 1fr)', alignItems: 'start' }}>
            <div>
              {cover
                ? <img src={cover} alt={article?.coverAlt ?? ''} style={{ display: 'block', width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--hair)' }} />
                : <div style={{ height: 120, borderRadius: 10, border: '1px dashed var(--ink-22)', display: 'grid', placeItems: 'center', color: 'var(--ink-45)', fontSize: 12 }}>No cover yet</div>}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={(e) => { const file = e.target.files?.[0]; if (file) void upload(file); }} />
                <button type="button" className="btn btn--xs" disabled={!!busy} onClick={() => fileRef.current?.click()}>{cover ? 'Replace cover' : 'Upload cover'}</button>
                {cover && <button type="button" className="btn btn--xs btn--danger" disabled={!!busy} onClick={() => confirmSimple(ctx, { title: 'Remove the cover', app: article?.slug, danger: true, body: 'The article shows no cover image until a new one is added.', confirmLabel: 'Remove cover', onConfirm: removeCover })}>Remove</button>}
              </div>
            </div>
            <Field label="Alt text" value={f.coverAlt} onChange={(v) => set('coverAlt', v)} placeholder="What the image shows" hint="Saved with the article." />
          </div>
        </DBlock>
        <DBlock title="Body" sub={`Markdown: h2/h3, paragraphs, lists, quotes, fenced code with a language, bold, code, links. Dashboard and API links as {{app}}/… and {{api}}/….${preview ? ` ${preview.readMinutes} min read.` : ''}`}>
          <div style={{ padding: '12px 14px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 12 }}>
            <textarea className="field__input mono" aria-label="Markdown body" value={f.body} spellCheck onChange={(e) => set('body', e.target.value)} style={{ minHeight: 420, resize: 'vertical', fontSize: 12.5, lineHeight: 1.5, marginTop: 0 }} placeholder={'## Where the delay comes from\n\nText…'} />
            <div style={{ minWidth: 0 }}>
              <div className="field__label" style={{ marginBottom: 7 }}>Preview{previewErr ? ` · ${previewErr}` : ''}</div>
              {preview && preview.toc.length > 0 && (
                <div style={{ fontSize: 11.5, color: 'var(--ink-62)', marginBottom: 10 }}>
                  Contents: {preview.toc.map((t) => t.text + (t.children.length ? ` (${t.children.map((c) => c.text).join(', ')})` : '')).join(' · ')}
                </div>
              )}
              <div className="art-preview" data-preview dangerouslySetInnerHTML={{ __html: preview?.html ?? '' }} />
            </div>
          </div>
        </DBlock>
      </DrawerBody>
    </Drawer>
  );
}
